import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { imageUrl } from '../../../../common/constants/ProductOfferingConstants.js';
import { dsClass } from '../../../../common/constants/dsClasses';
import { paths } from '../../../../common/constants/pathVariables.js';
import { OfferSubCategory, ShoppingBasketCoCoScope } from '../../../../common/enums';
import { parseBasketJson, useShoppingBasketCoCo } from '../../../../common/hooks/useShoppingBasketCoCo.js';
import {
  installationAddressMsg,
  orderHistoryMsg,
  productsMsg,
  returnToTheShopMsg,
  t,
} from '../../../../common/i18n/index.js';
import { getCompanyName } from '../../../../common/utils/accountUtils.js';
import { formatPhoneNumber } from '../../../../common/utils/phoneNumberUtils.js';
import { formatSumToString } from '../../../../common/utils/priceUtils.js';
import { isFixedBroadbandSubCategory } from '../../../../common/utils/productOfferingsUtils.js';
import { getContactInfo } from '../../../../common/utils/stateUtils.js';
import { DeliveryType } from '../../../../generated/api/deliveryType.js';
import { useAuth } from '../../../../public/site/AuthProvider.js';
import {
  clearShoppingBasketFromLocalstorage,
  getActiveAccountMasterId,
} from '../../../../selfservice/common/localStorageUtils.js';
import { clearShoppingBasketFromLocalstorageCoCo } from '../../../../selfservice/common/localStorageUtilsCoCo.js';
import { DUMMY_DEVICE_PICTURES } from '../../../DeviceDetailsCoCo/DeviceDetailsPictureCoCo.js';
import { Grid } from '../../../Grid/Grid.js';
import { PriceType } from '../../../HorizontalSubscriptionCard/types.js';
import { Loading } from '../../../Loading';
import { OrderNewsLetter } from '../../../OrderNewsLetter/OrderNewsLetter.js';
import {
  type StoredPlaceOrderCardPaymentData,
  clearFormDataFromSession,
  getFormDataFromSession,
} from '../../utils/cardPaymentUtilsCoCo.js';
import { getContactInfoLines, getDeliveryFields, getDeliveryMethodEntry } from '../../utils/placeOrderUtils.js';
import { getProductOfferAddOnPrices, isEppDeviceBasketItem } from '../../utils/placeOrderUtilsCoCo.js';
import { OrderInfo } from '../payment/OrderInfo.js';
import { ProductEntryView } from '../products/ProductEntry.js';
import { ProductEntryCoCo, ProductEntryView as ProductEntryViewCoCo } from '../products/ProductEntryCoCo.js';
import { ThankYouSummaryCoCo } from './ThankYouSummaryCoCo.js';
import type { AddonProductOfferingWithSelection } from '../../../../common/types/productOfferings.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../../../common/types/shoppingBasketCoCo.js';

import './PlaceOrderThankYou.scss';
import './PlaceOrderThankYouCoCo.scss';

const BundleAddonSummary = ({ addons }: { addons: AddonProductOfferingWithSelection[] }) => (
  <>
    {addons
      .filter(addon => addon.isChecked)
      .map(addon => (
        <li key={addon.id} className="of-place-order__addon-row">
          <ProductEntryViewCoCo
            productName={addon.name?.value ?? ''}
            productImage={<img src={imageUrl} alt={addon.name?.value ?? ''} />}
            price={
              <span>
                {
                  getProductOfferAddOnPrices(
                    addon.productOfferingPrice?.find(p => p.priceType === PriceType.RECURRING_CHARGE)
                      ?.priceWithoutTax ?? 0
                  ).value
                }
              </span>
            }
            disclaimerFields={<></>}
            description={<></>}
            readOnly
          />
        </li>
      ))}
  </>
);

const canViewOrderThankYou = (isAdminOrder: boolean, isAuthenticated: boolean, hasAuthenticatedUser: boolean) =>
  isAdminOrder ? isAuthenticated : hasAuthenticatedUser;

export const PlaceOrderThankYouCoCo = ({
  scope = ShoppingBasketCoCoScope.ADMIN,
}: { scope?: ShoppingBasketCoCoScope } = {}) => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<StoredPlaceOrderCardPaymentData | null>(null);
  const [preservedBasketItems, setPreservedBasketItems] = useState<ProductOfferingCartItems[] | null>(null);
  const { isAuthenticated, isLoading, authenticatedUser } = useAuth();
  const isAdminOrder = scope === ShoppingBasketCoCoScope.ADMIN;
  const { shoppingBasket: shoppingBasketJson } = useShoppingBasketCoCo(scope);
  const initialShoppingBasket: ShoppingBasketType = parseBasketJson(shoppingBasketJson);

  // Use preserved basket items if available, otherwise use current basket
  const shoppingBasket: ShoppingBasketType = preservedBasketItems
    ? { ...initialShoppingBasket, productOfferings: preservedBasketItems }
    : initialShoppingBasket;

  // Read session data and preserve basket items on mount
  useEffect(() => {
    const data = getFormDataFromSession();
    if (data) {
      setSessionData(data);
    }
    // Preserve basket items before any cleanup happens
    if (initialShoppingBasket.productOfferings && initialShoppingBasket.productOfferings.length > 0) {
      setPreservedBasketItems(initialShoppingBasket.productOfferings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount to capture initial state
  }, []);

  // Clear storage only after the page has rendered with the data and items are preserved
  useEffect(() => {
    if (sessionData && preservedBasketItems && preservedBasketItems.length > 0) {
      clearFormDataFromSession();

      if (isAdminOrder) {
        clearShoppingBasketFromLocalstorage();
      }
      clearShoppingBasketFromLocalstorageCoCo(scope);
    }
  }, [sessionData, preservedBasketItems, scope, isAdminOrder]);

  const handleReturnToShop = () => {
    navigate(isAdminOrder ? paths.WEB_SHOP : paths.EMPLOYEE_DEVICE_STORE);
  };

  const orderHistoryPath = isAdminOrder ? paths.CUSTOMER_ORDER_HOME : paths.EMPLOYEE_OWN_ORDERS;

  if (!canViewOrderThankYou(isAdminOrder, isAuthenticated, Boolean(authenticatedUser)) || !authenticatedUser) {
    return null;
  }

  if (isLoading) {
    return <Loading big={true} />;
  }

  const contactInfo = getContactInfo(authenticatedUser);
  // If user reloads page after successful order (storage already cleared), show simple thank you message
  if (!sessionData) {
    return (
      <Grid colWidthL={8}>
        <h1>{t.O4R6('Thank you for your order!')}</h1>
        <p>
          {t.YMF4('You can track your order in OmaElisa')} <a href={orderHistoryPath}>{t.GCO3(orderHistoryMsg)}</a>{' '}
          {t.YMF5('It may take a while for your order to appear.')}
        </p>
        <CL.Button
          className="of-order-info__return-button"
          color="primary"
          size="l"
          onClick={() => {
            handleReturnToShop();
          }}
        >
          {t.PLAW(returnToTheShopMsg)}
        </CL.Button>
        <OrderNewsLetter />
      </Grid>
    );
  }

  const deliveryType = sessionData?.formData.delivery.deliveryType;
  const deliveryInfo = deliveryType ? getDeliveryMethodEntry(deliveryType)?.description : undefined;
  const nothingToDeliver = deliveryType === DeliveryType.NONE;
  const deliveryAddress = sessionData?.formData.delivery.deliveryAddress;
  const deliveryFields = nothingToDeliver || !deliveryAddress ? [] : getDeliveryFields(deliveryAddress);
  const deliveryPrice = sessionData?.formData.delivery.deliveryPriceInCents;
  const orderId = sessionData?.orderId;
  const hasSoftwareProduct = shoppingBasket.productOfferings?.some(
    item => item.productOfferingType?.subcategory === OfferSubCategory.SOFTWARE
  );

  const hasEppDevice = Boolean(shoppingBasket.productOfferings?.some(isEppDeviceBasketItem));
  const isFixLine = Boolean(
    shoppingBasket.productOfferings?.some(item =>
      isFixedBroadbandSubCategory(item.productOfferingType?.subcategory ?? '')
    )
  );
  const { firstName, lastName, email, phoneNumber } = contactInfo;
  const orderingContactLines = isFixLine
    ? [
        getCompanyName(authenticatedUser, getActiveAccountMasterId()),
        `${firstName} ${lastName}`.trim(),
        email,
        phoneNumber && formatPhoneNumber(phoneNumber),
      ].filter((line): line is string => Boolean(line))
    : getContactInfoLines(contactInfo);
  const installationAddress = shoppingBasket.productOfferings?.find(
    item => item.qualificationAddressText
  )?.qualificationAddressText;
  const additionalDeliveryInfo = deliveryInfo ? (
    <>
      <h4>{t.GPPB('Delivery information')}</h4>
      <p>{deliveryInfo}</p>
    </>
  ) : undefined;
  // The Figma design orders the list as devices (+ their addons), then delivery, then accessories
  // (e.g. phone cases) - unlike the basket's natural order, which interleaves them. Split the
  // accessories out up front so they can be rendered in their own section after delivery below.
  const isAccessory = (item: ProductOfferingCartItems) =>
    item.productOfferingType?.subcategory === OfferSubCategory.QUANTITY_BASED_ACCESSORY;
  const accessories = hasEppDevice ? (shoppingBasket.productOfferings?.filter(isAccessory) ?? []) : [];
  const devices = hasEppDevice
    ? (shoppingBasket.productOfferings?.filter(item => !isAccessory(item)) ?? [])
    : (shoppingBasket.productOfferings ?? []);
  return (
    <Grid colWidthL={8}>
      <div
        className={classNames(
          'of-place-order__thank-you',
          hasEppDevice && 'of-place-order__thank-you--epp',
          isFixLine && 'of-place-order__thank-you--fix-line'
        )}
      >
        <h1>{t.O4R6('Thank you for your order!')}</h1>
        {hasSoftwareProduct && (
          <div className={dsClass.PADDING_BOTTOM_5}>
            <CL.Disclaimer
              title={t.K4NE('License allocation')}
              disclaimerType="info"
              icon={<CL.Icon icon="information" />}
              showCloseButton={true}
              text={t.E83W(
                'The orderer and the key user will receive a link to the F‑Secure portal. The licenses must be manually assigned before use.'
              )}
              visible
            />
          </div>
        )}

        <p>
          <b>
            <u>{orderId}</u>
          </b>{' '}
          – {t.ZDQ2('We have sent an order confirmation to your email {}.', contactInfo.email)}
        </p>
        <p>
          {t.YMF4('You can track your order in OmaElisa')} <a href={orderHistoryPath}>{t.GCO3(orderHistoryMsg)}</a>{' '}
          {t.YMF5('It may take a while for your order to appear.')}
        </p>
        <OrderInfo
          mainTitle={t.VDB5('Order summary')}
          orderContactInfo={{ fields: orderingContactLines }}
          deliveryInfo={{
            deliveryFields: deliveryFields,
            deliveryInfo: installationAddress ? (
              <div className="of-place-order__delivery-details">
                <div>{additionalDeliveryInfo}</div>
                <div>
                  <h4>{t.DI4V(installationAddressMsg)}</h4>
                  <div className="of-place-order__installation-address">
                    <CL.Icon icon="pin" type="filled" size="s" aria-hidden="true" />
                    <p>{installationAddress}</p>
                  </div>
                </div>
              </div>
            ) : (
              additionalDeliveryInfo
            ),
          }}
          gutter={false}
          headerLevel="h2"
          horizontalPadding={false}
          justifyCenter={false}
          showBorder={false}
        />
        <h3 className={dsClass.MARGIN_VERTICAL_0}>{t.IY4Z(productsMsg)}</h3>

        <ul className="ds-shoppingcart-wrapper">
          {devices.flatMap(basketItem => [
            <li key={basketItem.id} className={hasEppDevice ? 'of-place-order__device-row' : undefined}>
              <ProductEntryCoCo
                basketItem={basketItem}
                readOnly={true}
                imageOverride={hasEppDevice ? DUMMY_DEVICE_PICTURES[0]?.src : undefined}
              />
            </li>,
            // Addons are attached directly under their device, only for EPP-eligible devices.
            ...(isEppDeviceBasketItem(basketItem) &&
            (basketItem.bundledAddonProductOffering || basketItem.commercialAddonProductOffering)
              ? [
                  <BundleAddonSummary
                    key={`${basketItem.id}-addons`}
                    addons={[
                      ...(basketItem.bundledAddonProductOffering ?? []),
                      ...(basketItem.commercialAddonProductOffering ?? []),
                    ]}
                  />,
                ]
              : []),
          ])}
          {accessories.map(basketItem => (
            <li key={basketItem.id} className="of-place-order__accessory-row">
              <ProductEntryCoCo basketItem={basketItem} readOnly={true} />
            </li>
          ))}
          {deliveryType && !nothingToDeliver && (
            <li key="delivery" className="of-place-order__delivery-row">
              <ProductEntryView
                productName={getDeliveryMethodEntry(deliveryType)?.header ?? t.BSE3('Delivery')}
                productImage={<CL.Icon icon="delivery" size="xl" />}
                price={<>{formatSumToString(deliveryPrice ?? 0)}</>}
                disclaimerFields={<></>}
                description={<>{deliveryFields.join(', ')}</>}
              />
            </li>
          )}
        </ul>

        <ThankYouSummaryCoCo shoppingBasket={shoppingBasket} sessionData={sessionData}></ThankYouSummaryCoCo>

        <CL.Button
          className={classNames(dsClass.MARGIN_VERTICAL_4)}
          color="primary"
          size="l"
          onClick={() => {
            handleReturnToShop();
          }}
        >
          {t.PLAW(returnToTheShopMsg)}
        </CL.Button>
      </div>
      <OrderNewsLetter />
    </Grid>
  );
};
